import { ServiceLocator } from '../../app/ServiceLocator';
import { ResultHelper } from '../helpers/ResultHelper';
import { IErrorItem, MultipleItemResult } from '../models/MultipleItemResult';
import { Set } from '../models/Set';
import { IResult } from '../ServerApi/types/IResponse';
import {
  ContentActionNotification,
  IErrorDetail,
  IMultipleResultNotificationOptions,
  MultipleResultNotificationOptions,
  INotificationTranslationFns,
} from './ContentActionNotification';

export namespace ActionHelpers {

  // TODO (abrandon 2020-08-10): Remove this helper function once
  // ContentActionNotification is de-Angularized
  function getContentActionNotification() {
    return ServiceLocator.get<ContentActionNotification>('ContentActionNotification');
  }

  export function standardErrorDetails<T>(
    result: MultipleItemResult<T>,
    urlFn: (errorItem: T) => string,
    errorMessageFn: (errorItem: IErrorItem<T>) => string,
    itemNameFn: (item: T) => string
  ) {

    // Default item name function just uses 'item.name'
    itemNameFn = itemNameFn || ((item: any) => item.name);

    return result.errorItems().map((errorItem: IErrorItem<T>) => {
      return {
        itemName: itemNameFn(errorItem.item),
        itemUrl: urlFn(errorItem.item),
        errorMessage: errorMessageFn(errorItem)
      };
    });
  }

  export function standardErrorNotification<T>(
    result: MultipleItemResult<T>,
    translations: INotificationTranslationFns<T>,
    describeErrorDetail: (errorItem: IErrorItem<T>) => IErrorDetail,
  ): IMultipleResultNotificationOptions {

    return MultipleResultNotificationOptions.create(result,
      {
        describeSuccessSummary: (successCount: number) =>
          translations.success({ successCount, firstSuccessfulItem: result.firstSuccessfulItem() }),
        describeErrorSummary: (errorCount: number) =>
          translations.error({ errorCount, firstErrorItem: result.firstErrorItem() }),
        describeErrorDetail: describeErrorDetail
      }
    );
  }

  export function notifyStandardError<T>(
    items: Set<T>,
    result: IResult,
    translations: INotificationTranslationFns<T>,
    describeErrorDetail: (errorItem: IErrorItem<T>) => IErrorDetail,
  ) {
    if (result?.errors?.length > 0 && result.errors[0].id === undefined) {
      getContentActionNotification().notifyGenericError(result.errors[0]);
    } else {
      const itemResult = new MultipleItemResult(items, result);
      const errorNotificationOptions = standardErrorNotification(
        itemResult,
        translations,
        describeErrorDetail,
      );
      getContentActionNotification().notify(errorNotificationOptions);
    }

    return result; // to allow promise chaining
  }

  // When performing most actions, keep the selection except remove any items that were not found.
  // This helps to avoid "ghost" items in your selection - items that are still in your selection but are not in the list.
  export function deselectNotFound<T>(selectedItems: Set<T>, result: IResult, notFoundCode: number) {
    selectedItems.removeItemsIn(ResultHelper.getErrorIdsWithCode(result, notFoundCode));
  }

  // When performing delete-like actions, clear the selection, except keep any items that encountered an error other than "not found".
  // This allows you to see the items that failed and try again or perform some other action.
  export function deselectAllExceptErrors<T>(selectedItems: Set<T>, result: IResult, notFoundCode: number) {
    selectedItems.removeItemsNotIn(ResultHelper.getErrorIds(result, { excludeCode: notFoundCode }));
  }
}
